import { FC, Fragment, KeyboardEvent, MouseEvent, useCallback, useContext } from 'react'
import { ScreenContext } from 'react-components'

import UserMenu from './user-menu'
import {
    NavigationBarContent,
    DropdownButton,
    DropdownIcon,
    LoginButton,
    NavigationItem,
    NavigationItemText,
    UserButton,
    UserIcon,
    WishlistCount,
    WishlistIcon,
    WishlistIconContainer,
} from './navigation-bar-styles'
import { useHover } from '../../../hooks/dom'
import { useTranslation } from '../../../hooks/locale'
import { useWishlistCount } from '../../../hooks/wishlist'
import { NavigationTransport } from '../../../transports/common/navigation-section-transport'
import { useUserDetails } from '../../../hooks/users'

interface Props {
    navigation: NavigationTransport
    siteRelativeURL: string
}

const NavigationBar: FC<Props> = ({ navigation, siteRelativeURL }) => {
    const { wishlistButton, loginButton } = navigation

    const { isMobile } = useContext(ScreenContext)
    const { t } = useTranslation()

    const { userDetails } = useUserDetails({ additionalDetails: true })

    const finalWishlistCount = useWishlistCount()
    const _wishlistHasCount = finalWishlistCount > 0
    const _loginUrl = `${loginButton.url}?target=${siteRelativeURL}`

    const {
        isBeingHovered: isUserMenuOpen,
        onMouseEnter: onUserMenuMouseEnter,
        onMouseLeave: onUserMenuMouseLeave,
        onClick: onUserButtonClick,
        setIsBeingHovered: setUserMenuOpen,
    } = useHover()

    const _toggleUserMenuOpen = useCallback(
        (event: MouseEvent | KeyboardEvent) => {
            event.stopPropagation()
            setUserMenuOpen(isUserMenuOpen => !isUserMenuOpen)
        },
        [setUserMenuOpen],
    )

    return (
        <NavigationBarContent>
            {!!wishlistButton?.url && !isMobile && (
                <NavigationItem href={wishlistButton.url}>
                    <WishlistIconContainer hasCount={_wishlistHasCount}>
                        <WishlistIcon aria-label={t('wishlistIconText')} />
                        {_wishlistHasCount && <WishlistCount>{finalWishlistCount}</WishlistCount>}
                    </WishlistIconContainer>
                    <NavigationItemText>{wishlistButton.text}</NavigationItemText>
                </NavigationItem>
            )}
            {userDetails?.id ? (
                <UserButton
                    onMouseEnter={onUserMenuMouseEnter}
                    onMouseLeave={onUserMenuMouseLeave}
                    onClick={onUserButtonClick}
                >
                    <UserIcon aria-label={t('userIconText')} />
                    <NavigationItemText>{userDetails.firstName}</NavigationItemText>
                    {!isMobile && (
                        <DropdownButton>
                            <DropdownIcon aria-label={t('downIconText')} />
                        </DropdownButton>
                    )}
                    <UserMenu
                        isOpen={isUserMenuOpen}
                        toggleOpen={_toggleUserMenuOpen}
                        userDetails={userDetails}
                        navigation={navigation}
                    />
                </UserButton>
            ) : (
                <Fragment>
                    {isMobile ? (
                        <NavigationItem href={_loginUrl} noCrawl>
                            <UserIcon aria-label={t('userIconText')} />
                        </NavigationItem>
                    ) : (
                        <LoginButton href={_loginUrl} noCrawl>
                            <span>{t('register')}</span>
                        </LoginButton>
                    )}
                </Fragment>
            )}
        </NavigationBarContent>
    )
}

export default NavigationBar
